import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    formatAxisTooltip,
    formatChartCurrency,
    formatChartPercent,
    KpiCard,
    MetricBasis,
    MetricBasisToggle,
    moneyAmount,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

function pnoValue(entry, metricBasis) {
    return metricBasis === MetricBasis.Completed ? entry?.pnoNet : entry?.pnoGross;
}

function turnoverValue(entry, metricBasis) {
    return metricBasis === MetricBasis.Completed ? entry?.turnoverNet : entry?.turnoverGross;
}

function firstTotalEntry(data) {
    return Object.values(data?.pnoTotalByCurrency ?? {})[0] ?? null;
}

function firstGranularEntries(data) {
    return Object.values(data?.pnoGranularByCurrency ?? {})[0]?.pnoEntries ?? {};
}

function sortedDateKeys(...datasets) {
    return [...new Set(datasets.flatMap((dataset) => Object.keys(dataset ?? {})))]
        .sort((a, b) => a.localeCompare(b));
}

export default function ReportingPnoLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderPercentValue = useRenderPercentValue();
    const [totalPnoData, setTotalPnoData] = useState(null);
    const [withoutOfflinePnoData, setWithoutOfflinePnoData] = useState(null);
    const [channelsOnlyPnoData, setChannelsOnlyPnoData] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metricBasis, setMetricBasis] = useState(MetricBasis.Marketing);
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setTotalPnoData(null);
        setWithoutOfflinePnoData(null);
        setChannelsOnlyPnoData(null);
        setSnapshot(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.reportingPNO({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setTotalPnoData(nextData.total);
                setWithoutOfflinePnoData(nextData.withoutOffline);
                setChannelsOnlyPnoData(nextData.channelsOnly);
                setSnapshot(nextData.snapshot);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const totalEntry = firstTotalEntry(totalPnoData);
    const withoutOfflineEntry = firstTotalEntry(withoutOfflinePnoData);
    const channelsOnlyEntry = firstTotalEntry(channelsOnlyPnoData);
    const currency = totalEntry?.currency ?? withoutOfflineEntry?.currency ?? channelsOnlyEntry?.currency ?? projectCurrency(project);
    const totalPno = pnoValue(totalEntry, metricBasis);
    const withoutOfflinePno = pnoValue(withoutOfflineEntry, metricBasis);
    const channelsOnlyPno = pnoValue(channelsOnlyEntry, metricBasis);
    const offlineImpact = totalPno !== null && withoutOfflinePno !== null
        ? totalPno - withoutOfflinePno
        : null;
    const totalGranular = firstGranularEntries(totalPnoData);
    const withoutOfflineGranular = firstGranularEntries(withoutOfflinePnoData);
    const channelsOnlyGranular = firstGranularEntries(channelsOnlyPnoData);
    const dateKeys = sortedDateKeys(totalGranular, withoutOfflineGranular, channelsOnlyGranular);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const pnoTrendOption = {
        color: ["#2563eb", "#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 30, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartPercent),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dateKeys.map((date) => {
                const [, month, day] = date.split("-");
                return `${day}.${month}.`;
            }),
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartPercent},
        },
        series: [
            {
                name: t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoValue(totalGranular[date], metricBasis) ?? null),
            },
            {
                name: t("reporting_lab.pno.without_offline", {defaultValue: "Bez offline"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoValue(withoutOfflineGranular[date], metricBasis) ?? null),
            },
            {
                name: t("reporting_lab.pno.channels_only", {defaultValue: "Měřené kanály"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoValue(channelsOnlyGranular[date], metricBasis) ?? null),
            },
        ],
    };

    const spendTurnoverOption = {
        color: ["#7c3aed", "#2563eb"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 20, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dateKeys.map((date) => {
                const [, month, day] = date.split("-");
                return `${day}.${month}.`;
            }),
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: [
            {
                name: t("reporting_lab.pno.marketing_spend", {defaultValue: "Marketingové náklady"}),
                type: "bar",
                data: dateKeys.map((date) => moneyAmount(totalGranular[date]?.spendMarketing) / 100),
                barMaxWidth: 24,
            },
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: dateKeys.map((date) => moneyAmount(turnoverValue(totalGranular[date], metricBasis)) / 100),
                barMaxWidth: 24,
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.pno", {defaultValue: "PNO a marketingový mix"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.pno.title", {defaultValue: "K čemu slouží PNO a marketingový mix"})}
                    actions={<MetricBasisToggle value={metricBasis} onChange={setMetricBasis} metricBasisMeta={metricBasisMeta}/>}
                >
                    {t("reporting_lab.descriptions.pno.text", {defaultValue: "Ukazuje, kolik procent obratu spolykaly marketingové náklady, a porovnává výkon podle marketingové i obchodní báze objednávek."})}
                </ReportingPageDescription>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"})} value={renderPercentValue(totalPno)} meta={metricBasisMeta[metricBasis]?.shortLabel}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.without_offline", {defaultValue: "Bez offline"})} value={renderPercentValue(withoutOfflinePno)} meta={t("reporting_lab.pno.without_offline_meta", {defaultValue: "Bez ručních/offline nákladů"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.channels_only", {defaultValue: "Měřené kanály"})} value={renderPercentValue(channelsOnlyPno)} meta={t("reporting_lab.pno.channels_only_meta", {defaultValue: "Reklamní a měřené ostatní zdroje"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.offline_impact", {defaultValue: "Dopad offline"})} value={renderPercentValue(offlineImpact)} meta={t("reporting_lab.pno.offline_impact_meta", {defaultValue: "Rozdíl celkem vs. bez offline"})}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.pno.trend", {defaultValue: "Vývoj PNO v čase"})} className="col-span-12 xl:col-span-7">
                    <EChart option={pnoTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.pno.spend_vs_revenue", {defaultValue: "Marketingové náklady vs. obrat"})} className="col-span-12 xl:col-span-5">
                    <EChart option={spendTurnoverOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.pno.table", {defaultValue: "Rozpad podle měny"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.pno.currency", {defaultValue: "Měna"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.pno.without_offline", {defaultValue: "Bez offline"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.pno.channels_only", {defaultValue: "Měřené kanály"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.pno.marketing_spend", {defaultValue: "Marketingové náklady"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {Object.values(totalPnoData?.pnoTotalByCurrency ?? {}).map((entry) => {
                                const totalCurrencyEntry = entry;
                                const withoutOfflineCurrencyEntry = Object.values(withoutOfflinePnoData?.pnoTotalByCurrency ?? {}).find((item) => item.currency === entry.currency);
                                const channelsOnlyCurrencyEntry = Object.values(channelsOnlyPnoData?.pnoTotalByCurrency ?? {}).find((item) => item.currency === entry.currency);

                                return <tr key={entry.currency}>
                                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{entry.currency}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoValue(totalCurrencyEntry, metricBasis))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoValue(withoutOfflineCurrencyEntry, metricBasis))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoValue(channelsOnlyCurrencyEntry, metricBasis))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(totalCurrencyEntry?.spendMarketing, true)}</td>
                                    <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(turnoverValue(totalCurrencyEntry, metricBasis), true)}</td>
                                </tr>;
                            })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
